import {
	File,
	FileArchive,
	FileAudio,
	FileCode,
	FileImage,
	FileSpreadsheet,
	FileText,
	FileVideo,
	PenTool,
	Presentation,
	type LucideIcon,
} from 'lucide-react';

interface FileTypeMeta {
	/** Human-readable type label, e.g. "Excel Spreadsheet". */
	label: string;
	/** Lucide icon representing the file category. */
	Icon: LucideIcon;
}

const EXT_META: Record<string, FileTypeMeta> = {
	// Design / print formats — the bulk of a brand kit
	ai: { label: 'Illustrator File', Icon: PenTool },
	eps: { label: 'EPS Vector', Icon: PenTool },
	ps: { label: 'PostScript File', Icon: PenTool },
	svg: { label: 'SVG Vector', Icon: PenTool },
	sketch: { label: 'Sketch File', Icon: PenTool },
	fig: { label: 'Figma File', Icon: PenTool },
	psd: { label: 'Photoshop File', Icon: FileImage },
	indd: { label: 'InDesign File', Icon: FileImage },
	pdf: { label: 'PDF Document', Icon: FileText },
	// Spreadsheets
	xlsx: { label: 'Excel Spreadsheet', Icon: FileSpreadsheet },
	xls: { label: 'Excel Spreadsheet', Icon: FileSpreadsheet },
	xlsm: { label: 'Excel Spreadsheet', Icon: FileSpreadsheet },
	ods: { label: 'Spreadsheet', Icon: FileSpreadsheet },
	csv: { label: 'CSV File', Icon: FileSpreadsheet },
	tsv: { label: 'TSV File', Icon: FileSpreadsheet },
	// Documents
	doc: { label: 'Word Document', Icon: FileText },
	docx: { label: 'Word Document', Icon: FileText },
	odt: { label: 'Document', Icon: FileText },
	rtf: { label: 'Rich Text Document', Icon: FileText },
	pages: { label: 'Pages Document', Icon: FileText },
	// Presentations
	ppt: { label: 'PowerPoint', Icon: Presentation },
	pptx: { label: 'PowerPoint', Icon: Presentation },
	key: { label: 'Keynote', Icon: Presentation },
	odp: { label: 'Presentation', Icon: Presentation },
	// Archives
	zip: { label: 'ZIP Archive', Icon: FileArchive },
	rar: { label: 'RAR Archive', Icon: FileArchive },
	'7z': { label: '7-Zip Archive', Icon: FileArchive },
	tar: { label: 'TAR Archive', Icon: FileArchive },
	gz: { label: 'Gzip Archive', Icon: FileArchive },
	tgz: { label: 'Gzip Archive', Icon: FileArchive },
	// Text / code
	txt: { label: 'Text File', Icon: FileText },
	log: { label: 'Log File', Icon: FileText },
	md: { label: 'Markdown', Icon: FileText },
	markdown: { label: 'Markdown', Icon: FileText },
	json: { label: 'JSON File', Icon: FileCode },
	xml: { label: 'XML File', Icon: FileCode },
	yaml: { label: 'YAML File', Icon: FileCode },
	yml: { label: 'YAML File', Icon: FileCode },
	html: { label: 'HTML File', Icon: FileCode },
	css: { label: 'CSS File', Icon: FileCode },
	js: { label: 'JavaScript File', Icon: FileCode },
	ts: { label: 'TypeScript File', Icon: FileCode },
	// Media
	mp4: { label: 'Video', Icon: FileVideo },
	mov: { label: 'Video', Icon: FileVideo },
	webm: { label: 'Video', Icon: FileVideo },
	avi: { label: 'Video', Icon: FileVideo },
	mkv: { label: 'Video', Icon: FileVideo },
	mp3: { label: 'Audio', Icon: FileAudio },
	wav: { label: 'Audio', Icon: FileAudio },
	ogg: { label: 'Audio', Icon: FileAudio },
	flac: { label: 'Audio', Icon: FileAudio },
	m4a: { label: 'Audio', Icon: FileAudio },
};

/**
 * Resolves a friendly type label + icon for a file, used by both the header
 * meta line and the fallback card. Falls back to the MIME top-level type, then
 * to a generic file descriptor.
 */
/**
 * Formats identified by mime type, for the many assets whose stored name is a
 * user-typed label with no extension on it at all.
 */
const MIME_META: Record<string, FileTypeMeta> = {
	// One mime type covers .ai, .eps and .ps, so the label can't be more
	// specific than this when the name doesn't carry the extension.
	'application/postscript': { label: 'PostScript Vector', Icon: PenTool },
	'application/illustrator': { label: 'Illustrator File', Icon: PenTool },
	'application/x-illustrator': { label: 'Illustrator File', Icon: PenTool },
	'application/eps': { label: 'EPS Vector', Icon: PenTool },
	'application/x-eps': { label: 'EPS Vector', Icon: PenTool },
	'image/eps': { label: 'EPS Vector', Icon: PenTool },
	'application/pdf': { label: 'PDF Document', Icon: FileText },
	'image/vnd.adobe.photoshop': { label: 'Photoshop File', Icon: FileImage },
	'application/octet-stream': { label: 'File', Icon: File },
};

/** Aliases for mime subtypes that read badly in a short badge. */
const MIME_BADGE: Record<string, string> = {
	'application/postscript': 'EPS/AI',
	'application/illustrator': 'AI',
	'application/x-illustrator': 'AI',
	'application/octet-stream': 'FILE',
	'image/svg+xml': 'SVG',
};

/**
 * Extension of a filename, lowercased — empty when the name carries none.
 * Brand assets are stored under a user-typed label, so most have no extension:
 * a naive `split('.').pop()` returns the whole label and yields nonsense like
 * "COKE MEALS STICKERS COKE LOGO RED CMYK (1) File".
 */
export function extensionOf(name: string): string {
	const dot = name.lastIndexOf('.');
	if (dot <= 0 || dot === name.length - 1) return '';
	const ext = name.slice(dot + 1).toLowerCase();
	return /^[a-z0-9]{1,5}$/.test(ext) ? ext : '';
}

export function getFileTypeMeta(name: string, mimeType: string): FileTypeMeta {
	const ext = extensionOf(name);
	if (ext && EXT_META[ext]) return EXT_META[ext];

	const mime = mimeType.toLowerCase();
	if (MIME_META[mime]) return MIME_META[mime];

	if (mime.startsWith('video/')) return { label: 'Video', Icon: FileVideo };
	if (mime.startsWith('audio/')) return { label: 'Audio', Icon: FileAudio };
	if (mime.startsWith('text/')) return { label: 'Text File', Icon: FileText };
	if (mime.startsWith('image/')) return { label: 'Image', Icon: FileImage };

	return {
		label: ext ? `${ext.toUpperCase()} File` : 'File',
		Icon: File,
	};
}

/**
 * Short format label for a badge — "AI", "PDF", "SVG". Prefers the extension,
 * which is what a designer thinks in, and falls back to the mime subtype with
 * the unreadable ones aliased ("application/postscript" would otherwise render
 * as "POSTSCRIPT").
 */
export function getFormatLabel(name: string, mimeType: string): string {
	const ext = extensionOf(name);
	if (ext) return ext.toUpperCase();

	const mime = mimeType.toLowerCase();
	if (MIME_BADGE[mime]) return MIME_BADGE[mime];
	return mime.split('/').pop()?.toUpperCase().replace('SVG+XML', 'SVG') || 'FILE';
}

interface FileFallbackProps {
	name: string;
	mimeType: string;
	sizeLabel?: string | null;
}

export function FileFallback({ name, mimeType, sizeLabel }: FileFallbackProps) {
	const { label, Icon } = getFileTypeMeta(name, mimeType);

	return (
		<div className="bg-stripes flex h-full items-center justify-center p-8">
			<div className="flex max-w-[320px] flex-col items-center gap-4 text-center">
				<Icon size={44} strokeWidth={1.25} className="text-text-4" />

				<div className="flex flex-col items-center gap-1">
					<div className="max-w-full truncate text-base font-medium text-text-2">
						{name}
					</div>
					<div className="text-sm text-text-4">
						{label}
						{sizeLabel ? ` · ${sizeLabel}` : ''}
					</div>
				</div>

				<p className="text-sm leading-relaxed text-text-4">
					Preview isn&apos;t available for this file type. Use the download button above
					to save it.
				</p>
			</div>
		</div>
	);
}
